import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, orderBy, query, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import PDFThumbnail from '../components/PDFThumbnail';


import { 
  ArrowRightIcon,
  TrophyIcon,
  UserGroupIcon,
  ShoppingBagIcon,
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

const LandingPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedPdf, setSelectedPdf] = useState(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      const articlesRef = collection(db, 'articles');
      const q = query(articlesRef, orderBy('created_time', 'desc'), limit(6));
      const snapshot = await getDocs(q);
      const articlesData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setArticles(articlesData);
    } catch (error) {
      console.error('Error fetching articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };



  return (
    <div className="min-h-screen bg-gray-900">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background with gradient overlay */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://firebasestorage.googleapis.com/v0/b/oldham-dashboard1.firebasestorage.app/o/branding%2FlandingPageBanner.jpg?alt=media)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-gray-800/60 to-gray-900/80" />
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              Welcome to
              <br />
              <span className="text-oldham-gold">Oldham Athletics</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed">
              At Oldham Athletics, we're more than just a club—we're a family united by passion, perseverance, and a shared drive for excellence. 
              Here, you'll train with experienced coaches, guiding you to unlock your full potential.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link
                to="/programmes"
                className="bg-oldham-gold text-gray-900 hover:bg-oldham-gold/90 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                Join Our Club
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/about"
                className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center text-lg backdrop-blur-sm"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/60 rounded-full mt-2 animate-bounce"></div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Train with purpose.<br />
              Compete with pride.
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              But it's not just about the training; it's about the bonds we build along the way. 
              Whether you're pushing through a challenging workout or celebrating achievements, you'll find yourself surrounded by a supportive, inspiring team that lifts each other up.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <TrophyIcon className="w-10 h-10 text-oldham-gold" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Competitive Events</h3>
              <p className="text-gray-400 leading-relaxed">
                Join competitions representing Oldham Athletics throughout the year. 
                Compete with pride as part of our athletic family.
              </p>
            </div>
            
            <div className="group bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <UserGroupIcon className="w-10 h-10 text-oldham-blue" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Training Programs</h3>
              <p className="text-gray-400 leading-relaxed">
                Access structured training programs designed for athletes of all levels, from beginners to competitive athletes.
              </p>
            </div>
            
            <div className="group bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="w-16 h-16 flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <ShoppingBagIcon className="w-10 h-10 text-oldham-red" />
              </div>
              <h3 className="text-2xl font-bold text-white mb-4">Community</h3>
              <p className="text-gray-400 leading-relaxed">
                Join a supportive community of athletes and coaches dedicated to excellence and mutual growth.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Dauntless Articles Section */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Dauntless
            </h2>
            <p className="text-xl text-gray-400">
              Inspiring stories and insights from our athletic community
            </p>
          </div>
          
          {loading ? (
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-oldham-gold"></div>
            </div>
          ) : articles.length > 0 ? (
            <div className="relative">
              {/* Desktop: Grid Layout */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {articles.slice(0, 6).map((article) => (
                  <div key={article.id} className="group">
                    {article.pdf_url ? (
                      <div onClick={() => setSelectedPdf(article.pdf_url)}>
                        <PDFThumbnail
                          pdfUrl={article.pdf_url}
                          title={article.title}
                          description={article.excerpt || article.content?.substring(0, 150) + '...'}
                          onClick={() => setSelectedPdf(article.pdf_url)}
                        />
                      </div>
                    ) : (
                      <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2 h-full">
                        {article.image_url && (
                          <img 
                            src={article.image_url} 
                            alt={article.title}
                            className="w-full h-48 object-cover"
                          />
                        )}
                        <div className="p-8">
                          <div className="flex items-center text-sm text-gray-400 mb-4">
                            <CalendarIcon className="w-4 h-4 mr-2" />
                            {formatDate(article.created_time)}
                          </div>
                          <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary-400 transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-gray-400 mb-6 leading-relaxed">
                            {article.excerpt || article.content?.substring(0, 150) + '...'}
                          </p>
                          <Link
                            to={`/articles/${article.id}`}
                            className="text-oldham-gold hover:text-oldham-gold/80 font-semibold inline-flex items-center group-hover:translate-x-1 transition-all"
                          >
                            Read More
                            <ArrowRightIcon className="ml-2 h-4 w-4" />
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Mobile: Slideshow */}
              <div className="md:hidden relative">
                <div className="overflow-hidden">
                  <div 
                    className="flex transition-transform duration-300 ease-in-out"
                    style={{ transform: `translateX(-${currentSlide * 100}%)` }}
                  >
                    {articles.map((article) => (
                      <div key={article.id} className="w-full flex-shrink-0 px-4">
                        <div className="group">
                          {article.pdf_url ? (
                            <div onClick={() => setSelectedPdf(article.pdf_url)}>
                              <PDFThumbnail
                                pdfUrl={article.pdf_url}
                                title={article.title}
                                description={article.excerpt || article.content?.substring(0, 150) + '...'}
                                onClick={() => setSelectedPdf(article.pdf_url)}
                              />
                            </div>
                          ) : (
                            <div className="bg-gray-900/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
                              {article.image_url && (
                                <img 
                                  src={article.image_url} 
                                  alt={article.title}
                                  className="w-full h-48 object-cover"
                                />
                              )}
                              <div className="p-8">
                                <div className="flex items-center text-sm text-gray-400 mb-4">
                                  <CalendarIcon className="w-4 h-4 mr-2" />
                                  {formatDate(article.created_time)}
                                </div>
                                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary-400 transition-colors">
                                  {article.title}
                                </h3>
                                <p className="text-gray-400 mb-6 leading-relaxed">
                                  {article.excerpt || article.content?.substring(0, 150) + '...'}
                                </p>
                                <Link
                                  to={`/articles/${article.id}`}
                                  className="text-oldham-gold hover:text-oldham-gold/80 font-semibold inline-flex items-center group-hover:translate-x-1 transition-all"
                                >
                                  Read More
                                  <ArrowRightIcon className="ml-2 h-4 w-4" />
                                </Link>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Navigation Arrows */}
                {articles.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentSlide(currentSlide > 0 ? currentSlide - 1 : articles.length - 1)}
                      className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded-full transition-all z-10"
                    >
                      <ChevronLeftIcon className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => setCurrentSlide(currentSlide < articles.length - 1 ? currentSlide + 1 : 0)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded-full transition-all z-10"
                    >
                      <ChevronRightIcon className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Dots Indicator */}
                {articles.length > 1 && (
                  <div className="flex justify-center mt-6 space-x-2">
                    {articles.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${
                          index === currentSlide ? 'bg-oldham-gold' : 'bg-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-400 text-lg">No articles available at the moment.</p>
            </div>
          )}
          
          {/* View All Articles Button */}
          {articles.length > 0 && (
            <div className="text-center mt-12">
              <Link
                to="/articles"
                className="inline-flex items-center bg-oldham-gold hover:bg-oldham-gold/90 text-gray-900 font-semibold px-8 py-4 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                View All Articles
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </div>
          )}
        </div>
      </section>


      {/* Testimonials Section */}
      <section className="py-24 bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Journey with us
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Together, we chase goals, overcome obstacles, and create memories that last a lifetime. 
              Join us and become part of something extraordinary.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="group bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mb-4 mx-auto">
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/oldham-dashboard1.firebasestorage.app/o/athletes%2Fpraharsh.jpg?alt=media" 
                    alt="Praharsh Ryan"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "Ever grateful for coach Khai in coaching and giving me the hope and positivity when I came in after an injury which I thought will end my dreams."
                </p>
              </div>
              <div className="border-t border-gray-600 pt-4">
                <p className="text-white font-semibold">Praharsh Ryan</p>
                <p className="text-gray-400 text-sm">Athlete</p>
              </div>
            </div>
            
            <div className="group bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mb-4 mx-auto">
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/oldham-dashboard1.firebasestorage.app/o/athletes%2Fhaanee.png?alt=media" 
                    alt="Haanee Hamkah"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "This club has always been more than just a platform for competition; It's a community that has profoundly shaped my life. My biggest takeaway from representing Oldham Athletics has been the lifelong friendships I've made and the continued support I receive from those who stood beside me on the track."
                </p>
              </div>
              <div className="border-t border-gray-600 pt-4">
                <p className="text-white font-semibold">Haanee Hamkah</p>
                <p className="text-gray-400 text-sm">Athlete</p>
              </div>
            </div>
            
            <div className="group bg-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-primary-500/50 transition-all duration-300 hover:transform hover:-translate-y-2">
              <div className="mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden mb-4 mx-auto">
                  <img 
                    src="https://firebasestorage.googleapis.com/v0/b/oldham-dashboard1.firebasestorage.app/o/athletes%2Follie.jpg?alt=media" 
                    alt="Oliver Lim"
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 italic">
                  "I am thankful for my team Oldham Athletics for being my family, and for giving me space to rediscover myself and this sport."
                </p>
              </div>
              <div className="border-t border-gray-600 pt-4">
                <p className="text-white font-semibold">Oliver Lim</p>
                <p className="text-gray-400 text-sm">Athlete</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-24 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            Ready to join our athletic family?
          </h2>
          <p className="text-xl mb-12 text-gray-300 max-w-3xl mx-auto">
            Start your athletic journey with Oldham Athletics today. 
            Experience the passion, perseverance, and shared drive for excellence that makes us more than just a club—we're a family.
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link
              to="/programmes"
              className="bg-oldham-gold text-gray-900 hover:bg-oldham-gold/90 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              Join the Club
              <ArrowRightIcon className="ml-2 h-5 w-5" />
            </Link>
            <Link
              to="/team"
              className="border-2 border-white/20 text-white hover:bg-white/10 font-semibold py-4 px-8 rounded-lg transition-all duration-300 inline-flex items-center justify-center text-lg backdrop-blur-sm"
            >
              Meet the Team
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-gray-900 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Contact Us</h2>
            <p className="text-gray-400 mb-8">
              Have questions or need assistance? Get in touch with us using the email below.
            </p>
            <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 max-w-md mx-auto">
              <div className="flex items-center justify-center mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-oldham-gold to-oldham-goldDark rounded-full flex items-center justify-center mr-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-semibold">Email us at</p>
                  <a 
                    href="mailto:acs.oldham.athletics@gmail.com"
                    className="text-oldham-gold hover:text-oldham-gold/80 transition-colors"
                  >
                    acs.oldham.athletics@gmail.com
                  </a>
                </div>
              </div>
              <Link
                to="/contact"
                className="bg-white text-gray-900 hover:bg-gray-100 font-semibold py-3 px-6 rounded-lg transition-all duration-300 inline-flex items-center justify-center w-full"
              >
                Contact us
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer with Social Media */}
      <footer className="bg-gray-800 border-t border-gray-700 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-gray-400 text-sm mb-4 md:mb-0">
              © {new Date().getFullYear()} Oldham Athletics. All rights reserved.
            </div>
            <div className="flex items-center space-x-6">
              <a
                href="https://www.instagram.com/oldham.athletics"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center space-x-2 text-gray-400 hover:text-oldham-gold transition-colors"
              >
                <svg 
                  className="w-6 h-6 group-hover:scale-110 transition-transform" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
                <span className="font-medium">@oldham.athletics</span>
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* PDF Viewer Modal */}
      {selectedPdf && (
        <div className="fixed inset-0 bg-black z-50">
          <div className="w-full h-full relative">
            <button
              onClick={() => setSelectedPdf(null)}
              className="absolute top-4 right-4 z-10 bg-gray-800/80 hover:bg-gray-700/80 text-white p-2 rounded-full transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(selectedPdf)}&embedded=true`}
              className="w-full h-full"
              title="PDF Viewer"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
